/**
 * cdn-domain-origin-rewrite-tab.tsx — Origin Rewrite configuration tab (act-as).
 *
 * Mirrors Partner's OriginRewriteTab.tsx: master enabled toggle, collapsible
 * rule list (priority, match_type, match_path, multi-origin with weights,
 * per-rule headers, TLS). Mutations route through ActAsMutationConfirm.
 *
 * Endpoints: GET/PUT/DELETE /api/v3/cdn/domains/:id/origin-rewrite
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, ChevronDown, ChevronUp, ArrowRightLeft } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Skeleton,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNOriginRewrite,
  useDeleteActAsCDNOriginRewrite,
  actAsCDNCacheKeys,
  type OriginRewriteConfig,
} from '@/api/act-as-cdn-cache';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

/* ── Local types ─────────────────────────────────────────────────────────── */

interface OriginHeader {
  name: string;
  value: string;
}

interface RewriteRule {
  /** Ephemeral local ID for React keys — not sent to BE. */
  localId: string;
  name: string;
  priority: number;
  enabled: boolean;
  match_type: string;
  match_path: string;
  urls: string[];
  weights: number[];
  selector: string;
  host_header: string;
  tls_to_origin: boolean;
  sni_override: string;
  protocol: string;
  origin_headers: OriginHeader[];
  origin_tls_verify: boolean;
}

/* ── Static option lists ─────────────────────────────────────────────────── */

const MATCH_TYPES = [
  { value: 'prefix', label: 'Prefix' },
  { value: 'exact', label: 'Exact' },
  { value: 'suffix', label: 'Suffix' },
  { value: 'contains', label: 'Contains' },
  { value: 'wildcard', label: 'Wildcard (*/?)' },
  { value: 'regex', label: 'Regex' },
];

const PROTOCOLS = [
  { value: 'h1.1', label: 'HTTP/1.1' },
  { value: 'h2', label: 'HTTP/2' },
];

const SELECTORS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'failover', label: 'Failover' },
  { value: 'weighted_round_robin', label: 'Weighted' },
];

/* ── Helpers ─────────────────────────────────────────────────────────────── */

let _seq = 0;
const localId = () => `rule-${++_seq}`;

function emptyRule(priority: number): RewriteRule {
  return {
    localId: localId(),
    name: '',
    priority,
    enabled: true,
    match_type: 'prefix',
    match_path: '',
    urls: [''],
    weights: [1],
    selector: 'round_robin',
    host_header: '',
    tls_to_origin: false,
    sni_override: '',
    protocol: 'h1.1',
    origin_headers: [],
    origin_tls_verify: true,
  };
}

function hydrateRule(raw: Record<string, unknown>): RewriteRule {
  const r = raw as Record<string, unknown>;
  const urls: string[] =
    Array.isArray(r.urls) && (r.urls as string[]).length ? (r.urls as string[]) : [''];
  const weights: number[] =
    Array.isArray(r.weights) && (r.weights as number[]).length === urls.length
      ? (r.weights as number[])
      : urls.map(() => 1);
  return {
    localId: localId(),
    name: (r.name as string) ?? '',
    priority: (r.priority as number) ?? 100,
    enabled: (r.enabled as boolean) ?? true,
    match_type: (r.match_type as string) ?? 'prefix',
    match_path: (r.match_path as string) ?? '',
    urls,
    weights,
    selector: (r.selector as string) ?? 'round_robin',
    host_header: (r.host_header as string) ?? '',
    tls_to_origin: (r.tls_to_origin as boolean) ?? false,
    sni_override: (r.sni_override as string) ?? '',
    protocol: (r.protocol as string) ?? 'h1.1',
    origin_headers: Array.isArray(r.origin_headers)
      ? (r.origin_headers as OriginHeader[]).map((h) => ({
          name: (h.name as string) ?? '',
          value: (h.value as string) ?? '',
        }))
      : [],
    origin_tls_verify: (r.origin_tls_verify as boolean) ?? true,
  };
}

function ruleToPayload(r: RewriteRule): Record<string, unknown> {
  const finalUrls: string[] = [];
  const finalWeights: number[] = [];
  r.urls.forEach((u, i) => {
    if (u?.trim()) {
      finalUrls.push(u);
      finalWeights.push(Number(r.weights[i] ?? 1));
    }
  });
  return {
    name: r.name.trim(),
    priority: Number(r.priority),
    enabled: r.enabled,
    match_type: r.match_type,
    match_path: r.match_path,
    urls: finalUrls,
    weights: finalWeights,
    selector: r.selector,
    host_header: r.host_header,
    tls_to_origin: r.tls_to_origin,
    sni_override: r.sni_override,
    protocol: r.protocol,
    origin_headers: r.origin_headers.filter((h) => h.name.trim() !== ''),
    origin_tls_verify: r.origin_tls_verify,
  };
}

/* ── Rule item (collapsible) ─────────────────────────────────────────────── */

const grid2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
};

interface RuleItemProps {
  rule: RewriteRule;
  expanded: boolean;
  onToggle: () => void;
  onChange: (changes: Partial<RewriteRule>) => void;
  onDelete: () => void;
}

function RuleItem({ rule, expanded, onToggle, onChange, onDelete }: RuleItemProps) {
  const { t } = useTranslation();

  const setUrl = (idx: number, value: string) => {
    const urls = [...rule.urls];
    urls[idx] = value;
    onChange({ urls });
  };
  const setWeight = (idx: number, value: string) => {
    const weights = [...rule.weights];
    weights[idx] = Number(value) || 0;
    onChange({ weights });
  };
  const addUrl = () =>
    onChange({ urls: [...rule.urls, ''], weights: [...rule.weights, 1] });
  const removeUrl = (idx: number) =>
    onChange({
      urls: rule.urls.filter((_, i) => i !== idx),
      weights: rule.weights.filter((_, i) => i !== idx),
    });

  const addHeader = () =>
    onChange({ origin_headers: [...rule.origin_headers, { name: '', value: '' }] });
  const setHeader = (idx: number, key: 'name' | 'value', value: string) =>
    onChange({
      origin_headers: rule.origin_headers.map((h, i) =>
        i === idx ? { ...h, [key]: value } : h,
      ),
    });
  const removeHeader = (idx: number) =>
    onChange({ origin_headers: rule.origin_headers.filter((_, i) => i !== idx) });

  const weighted = rule.selector === 'weighted_round_robin';
  const originCount = rule.urls.filter((u) => u.trim()).length;

  return (
    <Card style={{ overflow: 'hidden' }}>
      {/* ── Collapsed header ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-3)',
          padding: 'var(--vn-space-2) var(--vn-space-3)',
        }}
      >
        <Switch
          checked={rule.enabled}
          onCheckedChange={(v) => onChange({ enabled: v })}
          aria-label={t('cdn_domain.origin_rewrite_tab.rule_enabled', 'Rule enabled')}
        />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--vn-space-2)',
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: 'var(--vn-font-size-md)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {rule.name || t('cdn_domain.origin_rewrite_tab.untitled_rule', 'Untitled rule')}
          </span>
          <Badge tone="neutral" appearance="subtle" size="sm">
            {t('cdn_domain.origin_rewrite_tab.priority_badge', 'priority {{n}}', {
              n: rule.priority,
            })}
          </Badge>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', flexShrink: 0 }}>
            {t('cdn_domain.origin_rewrite_tab.origin_count', '{{n}} origin', { n: originCount, count: originCount })}
          </span>
        </div>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={t('cdn_domain.origin_rewrite_tab.delete_rule', 'Delete rule')}
          onClick={onDelete}
        >
          <Trash2 size={15} />
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={expanded ? t('cdn_domain.origin_rewrite_tab.collapse_rule', 'Collapse') : t('cdn_domain.origin_rewrite_tab.expand_rule', 'Expand')}
          onClick={onToggle}
        >
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </IconButton>
      </div>

      {/* ── Expanded body ── */}
      {expanded && (
        <div
          style={{
            borderTop: '1px solid var(--vn-border-default)',
            padding: 'var(--vn-space-4)',
            display: 'grid',
            gap: 'var(--vn-space-4)',
          }}
        >
          {/* Name, priority, match */}
          <div style={grid2}>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_name', 'Name')}>
              {(p) => (
                <Input
                  {...p}
                  value={rule.name}
                  onChange={(e) => onChange({ name: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_priority', 'Priority')}>
              {(p) => (
                <NumericInput
                  {...p}
                  value={String(rule.priority)}
                  onChange={(e) => onChange({ priority: Number(e.target.value) || 0 })}
                />
              )}
            </Field>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_match_type', 'Match type')}>
              {(p) => (
                <Select value={rule.match_type} onValueChange={(v) => onChange({ match_type: v })}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MATCH_TYPES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_match_path', 'Match path')}>
              {(p) => (
                <Input
                  {...p}
                  value={rule.match_path}
                  placeholder="/api"
                  onChange={(e) => onChange({ match_path: e.target.value })}
                />
              )}
            </Field>
          </div>

          {/* Origin URLs */}
          <div>
            <CardTitle style={{ marginBottom: 'var(--vn-space-2)' }}>
              {t('cdn_domain.origin_rewrite_tab.section_origin_urls', 'Origin URLs')}
            </CardTitle>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {rule.urls.map((u, idx) => (
                <div
                  key={idx}
                  style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}
                >
                  <Input
                    value={u}
                    placeholder="https://origin.example.com"
                    onChange={(e) => setUrl(idx, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {weighted && (
                    <NumericInput
                      value={String(rule.weights[idx] ?? 1)}
                      onChange={(e) => setWeight(idx, e.target.value)}
                      style={{ width: 90 }}
                      aria-label={t('cdn_domain.origin_rewrite_tab.weight_label', 'Weight')}
                    />
                  )}
                  {rule.urls.length > 1 && (
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={t('cdn_domain.origin_rewrite_tab.remove_url', 'Remove URL')}
                      onClick={() => removeUrl(idx)}
                    >
                      <Trash2 size={15} />
                    </IconButton>
                  )}
                </div>
              ))}
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  startIcon={<Plus size={14} />}
                  onClick={addUrl}
                >
                  {t('cdn_domain.origin_rewrite_tab.add_origin_url', 'Add origin')}
                </Button>
              </div>
            </div>
          </div>

          {/* Connection overrides */}
          <div style={grid2}>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_lb', 'Load balancing')}>
              {(p) => (
                <Select value={rule.selector} onValueChange={(v) => onChange({ selector: v })}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SELECTORS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_protocol', 'Protocol')}>
              {(p) => (
                <Select value={rule.protocol} onValueChange={(v) => onChange({ protocol: v })}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROTOCOLS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_host_header', 'Host header')}>
              {(p) => (
                <Input
                  {...p}
                  value={rule.host_header}
                  onChange={(e) => onChange({ host_header: e.target.value })}
                />
              )}
            </Field>
            <Field label={t('cdn_domain.origin_rewrite_tab.field_sni', 'SNI override')}>
              {(p) => (
                <Input
                  {...p}
                  value={rule.sni_override}
                  onChange={(e) => onChange({ sni_override: e.target.value })}
                />
              )}
            </Field>
          </div>

          <SettingRow
            title={t('cdn_domain.origin_rewrite_tab.field_tls', 'TLS to origin')}
            control={
              <Switch
                checked={rule.tls_to_origin}
                onCheckedChange={(v) => onChange({ tls_to_origin: v })}
              />
            }
          />
          <SettingRow
            title={t('cdn_domain.origin_rewrite_tab.field_verify_cert', 'Verify origin certificate')}
            control={
              <Switch
                checked={rule.origin_tls_verify}
                onCheckedChange={(v) => onChange({ origin_tls_verify: v })}
              />
            }
          />

          {/* Custom headers to this rule's origin */}
          <div>
            <CardTitle style={{ marginBottom: 'var(--vn-space-2)' }}>
              {t('cdn_domain.origin_rewrite_tab.section_custom_headers', 'Custom headers to origin')}
            </CardTitle>
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {rule.origin_headers.map((h, idx) => (
                <div
                  key={idx}
                  style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}
                >
                  <Input
                    value={h.name}
                    placeholder={t('cdn_domain.origin_rewrite_tab.header_name', 'Header name')}
                    onChange={(e) => setHeader(idx, 'name', e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Input
                    value={h.value}
                    placeholder={t('cdn_domain.origin_rewrite_tab.header_value', 'Header value')}
                    onChange={(e) => setHeader(idx, 'value', e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <IconButton
                    variant="ghost"
                    size="sm"
                    aria-label={t('cdn_domain.origin_rewrite_tab.remove_header', 'Remove header')}
                    onClick={() => removeHeader(idx)}
                  >
                    <Trash2 size={15} />
                  </IconButton>
                </div>
              ))}
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  startIcon={<Plus size={14} />}
                  onClick={addHeader}
                >
                  {t('cdn_domain.origin_rewrite_tab.add_header', 'Add header')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface CdnDomainOriginRewriteTabProps {
  domainId: string;
}

export function CdnDomainOriginRewriteTab({ domainId }: CdnDomainOriginRewriteTabProps) {
  const { t } = useTranslation();
  const rewriteQ = useActAsCDNOriginRewrite(domainId);
  const deleteM = useDeleteActAsCDNOriginRewrite(domainId);

  const isConfigured = !!rewriteQ.data;

  const [masterEnabled, setMasterEnabled] = useState(false);
  const [rules, setRules] = useState<RewriteRule[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<RewriteRule | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);

  useEffect(() => {
    if (!rewriteQ.data) {
      setMasterEnabled(false);
      setRules([]);
      return;
    }
    setMasterEnabled(rewriteQ.data.enabled);
    setRules(
      (rewriteQ.data.rules as unknown as Record<string, unknown>[])
        .map(hydrateRule)
        .sort((a, b) => a.priority - b.priority),
    );
  }, [rewriteQ.data]);

  function addRule() {
    const nextPriority = rules.length
      ? Math.max(...rules.map((r) => r.priority)) + 10
      : 100;
    const r = emptyRule(nextPriority);
    setRules((prev) => [...prev, r]);
    setExpanded((prev) => new Set(prev).add(r.localId));
  }

  function patchRule(localId: string, changes: Partial<RewriteRule>) {
    setRules((prev) =>
      prev.map((r) => (r.localId === localId ? { ...r, ...changes } : r)),
    );
  }

  function toggleExpanded(localId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(localId) ? next.delete(localId) : next.add(localId);
      return next;
    });
  }

  function buildPayload(): OriginRewriteConfig {
    return {
      enabled: masterEnabled,
      rules: rules.map(ruleToPayload) as unknown as OriginRewriteConfig['rules'],
    };
  }

  function handleSave() {
    // Basic client-side validation.
    for (let i = 0; i < rules.length; i++) {
      const r = rules[i];
      if (!r.name.trim()) {
        // Surface the offending rule by expanding it.
        setExpanded((prev) => new Set(prev).add(r.localId));
        return;
      }
      if (!r.urls.some((u) => u.trim())) {
        setExpanded((prev) => new Set(prev).add(r.localId));
        return;
      }
    }
    setConfirmOpen(true);
  }

  if (rewriteQ.isLoading) return <Skeleton style={{ height: 280 }} />;
  if (rewriteQ.isError) {
    return (
      <TabError
        title={t('cdn_domain.origin_rewrite_tab.load_error', 'Failed to load origin rewrite configuration')}
        onRetry={() => rewriteQ.refetch()}
      />
    );
  }

  const saveSummary = t(
    'cdn_domain.origin_rewrite_tab.save_summary',
    'Update path-based origin routing — {{n}} rule(s) will be saved.',
    { n: rules.length },
  );
  const deleteAllSummary = t(
    'cdn_domain.origin_rewrite_tab.delete_summary',
    'Remove all origin rewrite rules from this domain.',
  );

  return (
    <Section
      title={t('cdn_domain.origin_rewrite_tab.title', 'Origin Rewrite')}
      description={t('cdn_domain.origin_rewrite_tab.description', 'Route URL paths to different origins for this domain.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        <Card>
          <CardHeader
            actions={
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={addRule}
              >
                {t('cdn_domain.origin_rewrite_tab.add_rule', 'Add rule')}
              </Button>
            }
          >
            <CardTitle>{t('cdn_domain.origin_rewrite_tab.section_rules', 'Rewrite rules')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.origin_rewrite_tab.section_rules_desc', '{{n}} rule(s) defined. Lower priority number wins on match.', { n: rules.length })}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <SettingRow
              title={t('cdn_domain.origin_rewrite_tab.field_master_enabled', 'Enabled')}
              description={t('cdn_domain.origin_rewrite_tab.field_master_enabled_desc', 'Master switch for path rewriting on this domain.')}
              control={
                <Switch checked={masterEnabled} onCheckedChange={setMasterEnabled} />
              }
            />

            {rules.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<ArrowRightLeft size={20} />}
                title={t('cdn_domain.origin_rewrite_tab.no_rules', 'No rewrite rules yet')}
                description={t('cdn_domain.origin_rewrite_tab.no_rules_desc', 'Click "Add rule" to create one.')}
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {rules.map((rule) => (
                  <RuleItem
                    key={rule.localId}
                    rule={rule}
                    expanded={expanded.has(rule.localId)}
                    onToggle={() => toggleExpanded(rule.localId)}
                    onChange={(changes) => patchRule(rule.localId, changes)}
                    onDelete={() => setPendingDelete(rule)}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            isConfigured ? (
              <Button
                variant="danger"
                onClick={() => setDeleteAllConfirmOpen(true)}
              >
                {t('cdn_domain.origin_rewrite_tab.action_remove', 'Remove')}
              </Button>
            ) : undefined
          }
          end={
            <Button onClick={handleSave}>
              {isConfigured
                ? t('cdn_domain.origin_rewrite_tab.action_save', 'Save')
                : t('cdn_domain.origin_rewrite_tab.action_create', 'Create')}
            </Button>
          }
        />
      </div>

      {/* ── Delete single rule confirmation (local-only, no server call) ── */}
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o: boolean) => !o && setPendingDelete(null)}
        title={t('cdn_domain.origin_rewrite_tab.confirm_rule_delete_title', 'Delete rewrite rule?')}
        description={
          pendingDelete?.name
            ? t('cdn_domain.origin_rewrite_tab.confirm_rule_delete_named', 'This removes "{{name}}" and its routing. Save to persist.', { name: pendingDelete.name })
            : t('cdn_domain.origin_rewrite_tab.confirm_rule_delete_unnamed', 'This removes the rule and its routing. Save to persist.')
        }
        confirmLabel={t('cdn_domain.origin_rewrite_tab.confirm_delete_btn', 'Delete')}
        destructive
        onConfirm={() => {
          if (pendingDelete) {
            setRules((prev) => prev.filter((r) => r.localId !== pendingDelete.localId));
          }
          setPendingDelete(null);
        }}
      />

      {/* ── Save (PUT) confirmation ── */}
      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          isConfigured
            ? t('cdn_domain.origin_rewrite_tab.confirm_save_title', 'Save origin rewrite')
            : t('cdn_domain.origin_rewrite_tab.confirm_create_title', 'Create origin rewrite')
        }
        summary={saveSummary}
        queryKeysToInvalidate={[actAsCDNCacheKeys.originRewrite(domainId)]}
        mutationFn={() =>
          actAsClient.put(`/cdn/domains/${domainId}/origin-rewrite`, buildPayload())
        }
      />

      {/* ── Delete all (DELETE) confirmation ── */}
      <ActAsMutationConfirm
        open={deleteAllConfirmOpen}
        onOpenChange={setDeleteAllConfirmOpen}
        title={t('cdn_domain.origin_rewrite_tab.confirm_delete_all_title', 'Remove origin rewrite configuration')}
        summary={deleteAllSummary}
        queryKeysToInvalidate={[actAsCDNCacheKeys.originRewrite(domainId)]}
        mutationFn={() => deleteM.mutateAsync()}
        onSuccess={() => {
          setRules([]);
          setMasterEnabled(false);
        }}
        destructive
        confirmLabel={t('cdn_domain.origin_rewrite_tab.action_remove', 'Remove')}
      />
    </Section>
  );
}
